"use client"

import * as React from "react"

import {
  getCategoryAncestors,
  type ExamCategory,
} from "@/lib/exam-categories"

export function expandedIdsForCategorySelection(
  categories: ExamCategory[],
  categoryIds: readonly string[],
  base: Record<string, boolean> = {},
): Record<string, boolean> {
  const next = { ...base }
  for (const id of categoryIds) {
    for (const ancestorId of getCategoryAncestors(categories, id)) {
      next[ancestorId] = true
    }
  }
  return next
}

export function scrollCategoryPickerToSelection(
  scroller: HTMLElement | null,
  behavior: ScrollBehavior = "auto",
): boolean {
  if (!scroller) return false
  const selected = scroller.querySelector<HTMLElement>(
    '[aria-selected="true"], [aria-current="true"]',
  )
  if (!selected) return false
  selected.scrollIntoView({ block: "center", behavior })
  return true
}

/** Scrolls the category list so the current selection is visible (after branches expand). */
export function useScrollCategoryPickerToSelection(
  scrollerRef: React.RefObject<HTMLElement | null>,
  {
    categoryIds,
    searchQuery,
    layoutKey,
  }: {
    categoryIds: readonly string[]
    searchQuery: string
    /** Re-run when expanded tree layout changes (e.g. mergedExpanded). */
    layoutKey: string
  },
) {
  const selectionKey = categoryIds.join("|")

  React.useLayoutEffect(() => {
    if (searchQuery.trim() || categoryIds.length === 0) return
    const scroller = scrollerRef.current
    if (!scroller) return

    let cancelled = false
    let frames = 0
    const run = () => {
      if (cancelled) return
      const done = scrollCategoryPickerToSelection(scroller)
      if (!done && frames < 20) {
        frames += 1
        requestAnimationFrame(run)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [categoryIds, layoutKey, scrollerRef, searchQuery, selectionKey])
}
